// 009 FR-031, FR-041..FR-043: one payment attempt. Create the checkout with the total the parent saw
// (one idempotency key per attempt, reused for retries of it), open the gateway, hand its result to
// the server, then read the checkout until it is final: every 2 s for up to 60 s.
import type { AppError } from "@/api/client";
import type { Api } from "@/api/types";

import { adapterFor } from "./payment";
import type { ShopClient } from "@/shop/shopApi";

export type PayOutcome =
  | { type: "paid"; checkout: Api["Checkout"] }
  | { type: "pending"; checkout: Api["Checkout"] }
  | {
      type: "underReview" | "expired" | "superseded";
      checkout: Api["Checkout"];
    }
  | { type: "failed" | "cancelled" | "unavailable" }
  | { type: "refused"; error: AppError };

export type PayDeps = {
  client: ShopClient;
  sleep?: (ms: number) => Promise<void>;
  pollEveryMs?: number;
  pollForMs?: number;
  /** "Confirming your payment…" starts. */
  onConfirming?: () => void;
};

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function pay(
  body: Api["CheckoutCreate"],
  idempotencyKey: string,
  {
    client,
    sleep = wait,
    pollEveryMs = 2000,
    pollForMs = 60_000,
    onConfirming,
  }: PayDeps,
): Promise<PayOutcome> {
  let checkout: Api["Checkout"];
  try {
    checkout = await client.createCheckout(body, idempotencyKey);
  } catch (e) {
    return { type: "refused", error: e as AppError };
  }
  const gateway = await adapterFor(checkout.payment.provider).open(
    checkout.payment,
  );
  if (gateway.type === "cancelled" || gateway.type === "unavailable")
    return { type: gateway.type };
  try {
    if (checkout.payment.provider === "fake") {
      checkout = await client.simulatePayment(
        checkout.id,
        gateway.type === "success" ? "SUCCESS" : "FAILURE",
      );
    } else if (gateway.type === "success" && gateway.result) {
      checkout = await client.reportPayment(checkout.id, gateway.result);
    }
  } catch (e) {
    return { type: "refused", error: e as AppError };
  }
  if (gateway.type === "failure") return { type: "failed" };
  onConfirming?.();
  for (let waited = 0; ; waited += pollEveryMs) {
    const final = finalOutcome(checkout);
    if (final) return final;
    if (waited >= pollForMs) return { type: "pending", checkout };
    await sleep(pollEveryMs);
    try {
      checkout = await client.getCheckout(checkout.id);
    } catch {
      // A failed read is retried at the next tick; the server's webhook stays the authority.
    }
  }
}

function finalOutcome(c: Api["Checkout"]): PayOutcome | null {
  switch (c.status) {
    case "PAID":
      return { type: "paid", checkout: c };
    case "FAILED":
      return { type: "failed" };
    case "UNDER_REVIEW":
      return { type: "underReview", checkout: c };
    case "EXPIRED":
      return { type: "expired", checkout: c };
    case "SUPERSEDED":
      return { type: "superseded", checkout: c };
    default:
      return null;
  }
}
